/**
 * Automations Page.
 *
 * Lists automation rules with enable/disable toggles, and provides a
 * builder for creating new rules. Supports three action types:
 * - Create Work Order (ad-hoc with title, auto-attaches matching inspection forms)
 * - Create Work Order from Template (uses a WO template with forms pre-attached)
 * - Update Status (transition to a known lifecycle status via select)
 *
 * Status fields use select dropdowns populated from the known asset lifecycle
 * statuses rather than free-text input.
 */

import { useState } from "react";
import { Zap, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import {
  useAutomationRules,
  useCreateAutomationRule,
  useUpdateAutomationRule,
  useDeleteAutomationRule,
} from "@/features/automations/api";
import { toast } from "@/stores/toastStore";
import {
  TRIGGER_ENTITY_TYPES,
  TRIGGER_EVENTS,
  ACTION_TYPES,
} from "@/types/automation";
import { useStatuses } from "@/features/inventory/api";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ActionDrawer from "@/components/ui/ActionDrawer";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface WOTemplate {
  id: string;
  name: string;
  work_type: string;
  title_template: string;
  form_template_ids: string[] | null;
}

function AutomationsPage() {
  const { data: rules, isLoading } = useAutomationRules();
  const createMutation = useCreateAutomationRule();
  const deleteMutation = useDeleteAutomationRule();
  // This organization's own lifecycle, not a copy of the seed list. A tenant
  // that renamed or extended its statuses was being offered names that did not
  // exist and never offered the ones that did (FS-015).
  const { data: assetStatuses } = useStatuses("asset");

  const { data: woTemplates } = useQuery<{ items: WOTemplate[] }>({
    queryKey: ["wo-templates"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/recurring-wos?page_size=100")).data,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [entityType, setEntityType] = useState("asset");
  const [event, setEvent] = useState("status_changed");
  const [statusTo, setStatusTo] = useState("");
  const [actionType, setActionType] = useState("create_work_order");
  const [woTitle, setWoTitle] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [templateId, setTemplateId] = useState("");

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const conditions: Record<string, string> = {};
    if (event === "status_changed" && statusTo) conditions.status_to = statusTo;

    const config: Record<string, unknown> = {};
    if (actionType === "create_work_order") {
      if (woTitle) config.title = woTitle;
      config.auto_attach_inspection_forms = true;
    } else if (actionType === "create_wo_from_template") {
      if (templateId) config.template_id = templateId;
    } else if (actionType === "update_status") {
      if (newStatus) config.new_status = newStatus;
    }

    try {
      await createMutation.mutateAsync({
        name,
        description: description || undefined,
        trigger_entity_type: entityType,
        trigger_event: event,
        trigger_conditions: conditions,
        action_type: actionType,
        action_config: config,
      });
      setShowCreate(false);
      setName("");
      setDescription("");
      setStatusTo("");
      setWoTitle("");
      setNewStatus("");
      setTemplateId("");
      toast.success("Automation rule created");
    } catch {
      toast.error("Failed to create automation rule");
    }
  };

  const templates = woTemplates?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Automations
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Define rules that trigger actions when events occur.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> New Rule
        </Button>
      </div>

      {/* Create Rule Drawer */}
      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Automation Rule"
        description="Define a trigger and action to automate your workflow."
        icon={Zap}
        size="lg"
      >
        <form onSubmit={handleCreate} className="space-y-5">
          <Input
            label="Rule Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder='e.g., "On Received → Inspect"'
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />

          {/* Trigger */}
          <div className="rounded-[var(--radius-md)] border border-[var(--fill-info)] bg-[var(--fill-info)] p-4">
            <h4 className="mb-3 text-xs font-semibold text-[var(--color-interactive-primary)] uppercase tracking-[0.1em]">
              When (Trigger)
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Entity
                </label>
                <select
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                  className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                >
                  {TRIGGER_ENTITY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Event
                </label>
                <select
                  value={event}
                  onChange={(e) => setEvent(e.target.value)}
                  className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                >
                  {TRIGGER_EVENTS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              {event === "status_changed" && (
                <div>
                  <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                    Status Changes To
                  </label>
                  {entityType === "asset" ? (
                    <select
                      value={statusTo}
                      onChange={(e) => setStatusTo(e.target.value)}
                      className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                    >
                      <option value="">Any status</option>
                      {(assetStatuses ?? []).map((s) => (
                        <option key={s.id} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={statusTo}
                      onChange={(e) => setStatusTo(e.target.value)}
                      placeholder="e.g., Completed"
                      className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action */}
          <div className="rounded-[var(--radius-md)] border border-[var(--text-success)]/30 bg-[var(--fill-success)] p-4">
            <h4 className="mb-3 text-xs font-semibold text-[var(--text-success)] uppercase tracking-[0.1em]">
              Then (Action)
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Action
                </label>
                <select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                >
                  {ACTION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {actionType === "create_work_order" && (
                <Input
                  label="Work Order Title"
                  value={woTitle}
                  onChange={(e) => setWoTitle(e.target.value)}
                  placeholder="e.g., Intake Inspection"
                />
              )}

              {actionType === "create_wo_from_template" && (
                <div>
                  <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                    Work Order Template
                  </label>
                  {templates.length > 0 ? (
                    <select
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value)}
                      required
                      className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                    >
                      <option value="">Select a template...</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                          {t.form_template_ids
                            ? ` (${t.form_template_ids.length} form${t.form_template_ids.length !== 1 ? "s" : ""})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="mt-1.5 text-xs text-[var(--text-tertiary)]">
                      No work order templates found. Create one on the Recurring
                      tab in Maintenance first.
                    </p>
                  )}
                </div>
              )}

              {actionType === "update_status" && (
                <div>
                  <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                    New Status
                  </label>
                  {entityType === "asset" ? (
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      required
                      className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                    >
                      <option value="">Select status...</option>
                      {(assetStatuses ?? []).map((s) => (
                        <option key={s.id} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      placeholder="e.g., Completed"
                      className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                    />
                  )}
                </div>
              )}
            </div>

            {actionType === "create_work_order" && (
              <p className="mt-3 text-xs text-[var(--text-tertiary)]">
                Matching inspection forms will be automatically attached based
                on the asset's category.
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" isLoading={createMutation.isPending}>
              Create Rule
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {/* Rules List */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : rules && rules.length > 0 ? (
        <div className="space-y-3">
          {rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onDelete={() =>
                deleteMutation.mutate(rule.id, {
                  onSuccess: () => toast.success("Automation rule deleted"),
                  onError: () => toast.error("Failed to delete rule"),
                })
              }
            />
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Zap />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No automation rules
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create a rule to automate your workflows.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

function RuleCard({
  rule,
  onDelete,
}: {
  rule: {
    id: string;
    name: string;
    description: string | null;
    is_active: boolean;
    trigger_entity_type: string;
    trigger_event: string;
    trigger_conditions: Record<string, unknown>;
    action_type: string;
    action_config: Record<string, unknown>;
  };
  onDelete: () => void;
}) {
  const updateMutation = useUpdateAutomationRule(rule.id);

  const toggleActive = () => {
    updateMutation.mutate(
      { is_active: !rule.is_active },
      {
        onSuccess: () =>
          toast.success(rule.is_active ? "Rule disabled" : "Rule enabled"),
        onError: () => toast.error("Failed to update rule"),
      },
    );
  };

  const actionLabel = () => {
    if (rule.action_type === "create_work_order") {
      const title = rule.action_config.title;
      return title ? `Create WO: "${title}"` : "Create Work Order";
    }
    if (rule.action_type === "create_wo_from_template") {
      return "Create WO from template";
    }
    if (rule.action_type === "update_status") {
      const status = rule.action_config.new_status;
      return status ? `Set status → "${status}"` : "Update Status";
    }
    return rule.action_type.replace(/_/g, " ");
  };

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Zap
              className={`h-5 w-5 ${rule.is_active ? "text-yellow-500" : "text-[var(--text-tertiary)]"}`}
            />
            <h3 className="font-semibold text-[var(--text-primary)]">
              {rule.name}
            </h3>
            <Badge variant={rule.is_active ? "success" : "default"}>
              {rule.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
          {rule.description && (
            <p className="mt-1 ml-8 text-sm text-[var(--text-secondary)]">
              {rule.description}
            </p>
          )}

          <div className="mt-3 ml-8 flex flex-wrap gap-2 text-xs">
            <span className="rounded-[var(--radius-md)] bg-[var(--fill-info)] px-2.5 py-1 text-[var(--color-interactive-primary)] font-medium">
              When {rule.trigger_entity_type}{" "}
              {rule.trigger_event.replace("_", " ")}
              {"status_to" in rule.trigger_conditions && (
                <>
                  {" "}
                  → <strong>{String(rule.trigger_conditions.status_to)}</strong>
                </>
              )}
            </span>
            <span className="text-[var(--text-tertiary)] self-center">→</span>
            <span className="rounded-[var(--radius-md)] bg-[var(--fill-success)] px-2.5 py-1 text-[var(--text-success)] font-medium">
              {actionLabel()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleActive}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${rule.is_active ? "bg-[var(--color-interactive-primary)]" : "bg-[var(--surface-sunken)]"}`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-[var(--surface-base)] transition-transform ${rule.is_active ? "translate-x-6" : "translate-x-1"}`}
            />
          </button>
          <button
            onClick={onDelete}
            className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Card>
  );
}

export default AutomationsPage;
